import { useEffect, useState, useRef } from "react";
import { FiMail, FiMenu, FiX } from "react-icons/fi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import productsData from "../Data/products.json";
import { FiChevronRight, FiChevronDown } from "react-icons/fi";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [hoveredCategoryId, setHoveredCategoryId] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownHovered, setDropdownHovered] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // Theme colors
  const gold = "#fdca00";
  const blue = "#0393da";
  const white = "#fff";
  const dropBg = "#f9fafc";
  const hoverCategory = "#fdca00";
  const hoverItem = "#0393da";
  const highlightBg = "#fff5d6";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (productsData && Array.isArray(productsData.products)) {
      setCategories(productsData.products);
    }
  }, []);

  // Prevent body scrolling when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [menuOpen]);

  const isHome = location.pathname === "/" || location.pathname === "";
  const isAbout = location.pathname === "/about";
  const isProducts = location.pathname.startsWith("/products");
  const isContact = location.pathname === "/contact";

  const handleLinkClick = () => {
    setMenuOpen(false);
    setDropdownOpen(false);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  // New: ensures window reloads when clicking a category link
  const handleCategoryLinkClick = (cat) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    setDropdownOpen(false);
    // Use navigate (in case already on category) before reload to update URL and then reload
    navigate(categoryLink(cat), { replace: false });
    window.location.reload();
  };

  // For subcategory links to category too (in products dropdown)
  const handleCategoryLinkClickByPath = (path) => (e) => {
    e.preventDefault();
    setMenuOpen(false);
    setDropdownOpen(false);
    navigate(path, { replace: false });
    window.location.reload();
  };

  // --- Improved Desktop ProductsDropdown with category links on each product (and categories) ---
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    function handleKeyDown(e) {
      if (e.key === "Escape") {
        setDropdownOpen(false);
        setHoveredCategoryId(null);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [dropdownOpen]);

  useEffect(() => {
    if (!dropdownOpen) return;
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setHoveredCategoryId(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen]);

  const handleProductsMouseEnter = () => {
    setDropdownHovered(true);
    setDropdownOpen(true);
  };

  const handleProductsMouseLeave = () => {
    setDropdownHovered(false);
    setTimeout(() => {
      if (!dropdownHovered) {
        setDropdownOpen(false);
        setHoveredCategoryId(null);
      }
    }, 40);
  };

  const handleCategoryMouseEnter = (catId) => {
    setHoveredCategoryId(catId);
  };

  const handleProductsAreaMouseEnter = () => setDropdownHovered(true);
  const handleProductsAreaMouseLeave = () => {
    setDropdownHovered(false);
    setTimeout(() => {
      if (!dropdownHovered) {
        setDropdownOpen(false);
        setHoveredCategoryId(null);
      }
    }, 40);
  };

  // Util to slugify or similar for Footer-style category links ("/products/" + category.slug)
  function categoryLink(cat) {
    if (cat.slug) return `/products/${cat.slug}`;
    // fallback if no slug: kebab-case the category name
    return `/products/#${cat.categoryName
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, "")}`;
  }

  // Products Dropdown - with /products link and category links like Footer
  const productsDropdown = (
    <div
      ref={dropdownRef}
      className="group relative"
      tabIndex={0}
      onMouseEnter={handleProductsMouseEnter}
      onMouseLeave={handleProductsMouseLeave}
      onFocus={() => setDropdownOpen(true)}
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget)) {
          setDropdownOpen(false);
          setHoveredCategoryId(null);
        }
      }}
    >
      {/* Wrap button in a Link to "/products" */}
      <Link
        to="/products"
        className={`hover:text-[${gold}] ${
          isProducts
            ? `text-[${gold}] border-b-2 border-[${gold}] pb-1 md:pb-0 md:border-b-0`
            : scrolled
            ? "text-black"
            : "text-white"
        } font-medium flex items-center gap-1 focus:outline-none`}
        style={isProducts ? { color: gold, borderBottomColor: gold } : undefined}
        aria-haspopup="true"
        aria-expanded={dropdownOpen}
        tabIndex={0}
        onClick={e => {
          setDropdownOpen(true);
          handleLinkClick();
        }}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            setDropdownOpen(open => !open);
          }
        }}
      >
        <span className="font-serif">Products</span>
        <svg className={`inline mt-[2px] transition-transform ${dropdownOpen ? "rotate-180" : ""}`} width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      </Link>
      {dropdownOpen && (
        <div
          className="absolute top-1/2 left-0 mt-3 flex z-50 flex-row animate-fade-down"
          style={{
            background: dropBg,
            boxShadow: `0 8px 28px -8px rgba(3,57,119,0.16), 0 2px 8px 0 rgba(70,70,70,0.07)`,
            borderRadius: "16px",
            border: "1px solid #eaeaea",
            padding: "1.3rem 1.6rem",
            minWidth: 350,
          }}
          onMouseEnter={handleProductsAreaMouseEnter}
          onMouseLeave={handleProductsAreaMouseLeave}
        >
          {/* Categories (left column) */}
          <ul className="flex flex-col gap-1 mr-6 min-w-[170px]">
            {/* All Products link */}
            <li>
              <Link
                to="/products"
                className="py-1.5 px-3 rounded-md cursor-pointer font-medium font-serif transition-colors"
                style={{
                  color: blue,
                  background: isProducts ? highlightBg : undefined,
                  border: isProducts ? `1.5px solid ${gold}` : "1.5px solid transparent",
                  outline: isProducts ? `2px solid ${gold}` : undefined,
                  marginBottom: "0.5rem"
                }}
                onClick={handleLinkClick}
                role="menuitem"
              >
                <span className="whitespace-nowrap">All Products</span>
              </Link>
            </li>
            {categories.map((cat, idx) => (
              <li
                key={cat.categoryId}
                className={`py-1.5 px-3 rounded-md cursor-pointer font-medium font-serif transition-colors group/category`}
                style={{
                  background:
                    hoveredCategoryId === cat.categoryId
                      ? highlightBg
                      : undefined,
                  color:
                    hoveredCategoryId === cat.categoryId
                      ? blue
                      : "#212d42",
                  border:
                    hoveredCategoryId === cat.categoryId
                      ? `1.5px solid ${gold}`
                      : "1.5px solid transparent",
                  boxShadow:
                    hoveredCategoryId === cat.categoryId
                      ? `0 2px 6px 0 ${gold}26`
                      : undefined,
                  outline:
                    hoveredCategoryId === cat.categoryId
                      ? `2px solid ${gold}`
                      : undefined,
                }}
                onMouseEnter={() => handleCategoryMouseEnter(cat.categoryId)}
                onFocus={() => setHoveredCategoryId(cat.categoryId)}
                onBlur={() => setHoveredCategoryId(null)}
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    setHoveredCategoryId(cat.categoryId);
                  } else if (e.key === "ArrowDown") {
                    const next = categories[(idx + 1) % categories.length];
                    setHoveredCategoryId(next.categoryId);
                  } else if (e.key === "ArrowUp") {
                    const prev =
                      categories[(idx - 1 + categories.length) % categories.length];
                    setHoveredCategoryId(prev.categoryId);
                  }
                }}
                aria-controls={`products-dropdown-list-${cat.categoryId}`}
                aria-selected={hoveredCategoryId === cat.categoryId}
                role="menuitem"
              >
                {/* Category link same as Footer */}
                <Link
                  to={categoryLink(cat)}
                  style={{
                    color:
                      hoveredCategoryId === cat.categoryId
                        ? blue
                        : "#212d42",
                    transition: "color .19s",
                    display: "block",
                    textDecoration: "none",
                  }}
                  onClick={handleCategoryLinkClick(cat)}
                  tabIndex={-1}
                  className="whitespace-nowrap"
                >
                  {cat.categoryName}
                </Link>
              </li>
            ))}
          </ul>
          {/* Products (right column) */}
          <div
            className="border-l border-gray-200 pl-5 flex flex-col justify-start"
            style={{
              display: hoveredCategoryId ? "block" : "none",
              minWidth: 220,
            }}
          >
            {categories.map(
              (cat) =>
                hoveredCategoryId === cat.categoryId && (
                  <ul
                    key={cat.categoryId}
                    id={`products-dropdown-list-${cat.categoryId}`}
                    className="flex flex-col gap-0.5 pt-0.5"
                    role="menu"
                  >
                    {/* Category link at the top for this category */}
                    <li
                      className="py-1.5 px-3 rounded-md font-semibold"
                      style={{
                        color: blue,
                        background: "#fcfcfbe6",
                        marginBottom: "0.2em",
                        border: `1.5px solid ${gold}`,
                        fontFamily: "serif"
                      }}
                      role="none"
                    >
                      <Link
                        to={categoryLink(cat)}
                        style={{
                          textDecoration: "none",
                          color: blue,
                        }}
                        onClick={handleCategoryLinkClick(cat)}
                        className="whitespace-nowrap"
                      >
                        View all {cat.categoryName}
                      </Link>
                    </li>
                    {/* The products (each linking to category link as well as product) */}
                    {cat.subProducts && cat.subProducts.length > 0 ? (
                      cat.subProducts.map((prod) => (
                        <li
                          key={prod.productId}
                          className="py-1 px-3 rounded-md transition-colors"
                          style={{
                            fontSize: "1.05em",
                          }}
                          role="none"
                        >
                          <Link
                            to={categoryLink(cat)}
                            style={{
                              color: "#232d3b",
                              textDecoration: "none",
                              borderRadius: 6,
                              display: "block",
                              transition: "color .13s,background .15s",
                              padding: "3.5px 2px",
                            }}
                            className="group/subproduct"
                            onClick={handleCategoryLinkClick(cat)}
                            role="menuitem"
                            onMouseOver={e => {
                              e.currentTarget.style.color = hoverItem;
                              e.currentTarget.style.background = "#e6f6ff";
                            }}
                            onMouseOut={e => {
                              e.currentTarget.style.color = "#232d3b";
                              e.currentTarget.style.background = "";
                            }}
                          >
                            <span className="font-serif">
                              {prod.productName}
                            </span>
                          </Link>
                        </li>
                      ))
                    ) : (
                      <li
                        className="text-gray-500 text-xs italic py-2 px-2"
                        style={{
                          color: "#889",
                        }}
                        role="none"
                      >
                        No products
                      </li>
                    )}
                  </ul>
                )
            )}
          </div>
        </div>
      )}
    </div>
  );

  // --- Mobile Products Nested List with main /products and category links and per-product category links ---
  // REWRITE FROM HERE for mobile menu (Products expandable by arrow only)

  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [mobileCatId, setMobileCatId] = useState(null);

  // Handler for arrow click on Products (mobile)
  function handleMobileProductsArrow(e) {
    e.preventDefault();
    setMobileExpanded((prev) => !prev);
  }

  // Handler for arrow click on each category (mobile)
  function handleMobileCategoryArrow(catId, e) {
    e.preventDefault();
    setMobileCatId((prev) => (prev === catId ? null : catId));
  }

  const mobileProductsList = (
    <div>
      {/* Top-level: "Products" with arrow as main header */}
      <div className="flex items-center w-full font-medium mb-2">
        {/* The Products label/name opens the /products page */}
        <Link
          to="/products"
          className="flex-grow flex items-center gap-1"
          style={
            isProducts
              ? { color: gold, borderBottom: `2px solid ${gold}` }
              : { color: blue }
          }
          onClick={() => {
            setMobileExpanded(false);
            handleLinkClick();
          }}
        >
          <span className="font-serif">Products</span>
        </Link>
        {/* The arrow icon opens the nested list */}
        <button
          type="button"
          aria-label={mobileExpanded ? "Collapse product categories" : "Expand product categories"}
          className="ml-2 p-1"
          style={{ color: isProducts ? gold : blue }}
          onClick={handleMobileProductsArrow}
        >
          {mobileExpanded ? (
            <FiChevronDown size={20} />
          ) : (
            <FiChevronRight size={20} />
          )}
        </button>
      </div>
      {/* Nested expandable category/product list */}
      {mobileExpanded && (
        <div
          className="ml-2 border-l-2 border-gray-100"
          style={{
            background: "#fafcfe",
            borderRadius: 9,
            boxShadow: "0 1px 8px #efefefa6",
            padding: ".45rem 0 .2rem .4rem",
          }}
        >
          <ul className="flex flex-col gap-1">
            {categories.map((cat) => (
              <li key={cat.categoryId}>
                <div className="flex items-center w-full">
                  {/* Category name - go to category page if clicked */}
                  <Link
                    to={categoryLink(cat)}
                    className={`flex-grow flex items-center py-1 px-3 rounded font-semibold text-left`}
                    style={{
                      color: blue,
                      background:
                        mobileCatId === cat.categoryId ? highlightBg : undefined,
                      border:
                        mobileCatId === cat.categoryId
                          ? `1.5px solid ${gold}`
                          : "1.5px solid transparent",
                      fontFamily: "serif",
                      transition: "background .13s, border .13s",
                    }}
                    onClick={handleCategoryLinkClick(cat)}
                  >
                    {cat.categoryName}
                  </Link>
                  {/* Arrow - expand/collapse products list under category (not navigating) */}
                  <button
                    type="button"
                    className="ml-2 p-1"
                    aria-label={
                      mobileCatId === cat.categoryId
                        ? `Collapse ${cat.categoryName} products`
                        : `Expand ${cat.categoryName} products`
                    }
                    style={{
                      color: blue,
                      background: "transparent",
                      border: "none",
                      outline: "none",
                      cursor: "pointer"
                    }}
                    onClick={(e) => handleMobileCategoryArrow(cat.categoryId, e)}
                  >
                    {mobileCatId === cat.categoryId ? (
                      <FiChevronDown size={16} />
                    ) : (
                      <FiChevronRight size={16} />
                    )}
                  </button>
                </div>
                {/* On expansion, show per-product links (that all go to category link as specified) */}
                {mobileCatId === cat.categoryId && (
                  <ul className="pl-4 my-2 border-l border-gray-100">
                    {/* "View all products" under category for emphasis */}
                    <li className="mb-1 last:mb-0">
                      <Link
                        to={categoryLink(cat)}
                        style={{
                          fontSize: "0.98em",
                          color: blue,
                          whiteSpace: "nowrap",
                          padding: "2.5px 3px",
                          borderRadius: 4,
                          textDecoration: "none",
                          display: "block",
                          fontWeight: 600,
                          fontFamily: "serif"
                        }}
                        onClick={handleCategoryLinkClick(cat)}
                      >
                        View all {cat.categoryName}
                      </Link>
                    </li>
                    {cat.subProducts && cat.subProducts.length > 0 ? (
                      cat.subProducts.map((prod) => (
                        <li
                          key={prod.productId}
                          className="mb-1 last:mb-0"
                          style={{ borderRadius: 4 }}
                        >
                          <Link
                            to={categoryLink(cat)}
                            className=""
                            style={{
                              fontSize: "0.97em",
                              color: blue,
                              whiteSpace: "nowrap",
                              padding: "2.5px 3px",
                              borderRadius: 4,
                              textDecoration: "none",
                              display: "block",
                              transition: "color .14s,background .13s",
                              fontFamily: "serif"
                            }}
                            onClick={handleCategoryLinkClick(cat)}
                            onMouseOver={e => {
                              e.currentTarget.style.color = hoverItem;
                              e.currentTarget.style.background = "#e6f6ff";
                            }}
                            onMouseOut={e => {
                              e.currentTarget.style.color = blue;
                              e.currentTarget.style.background = "";
                            }}
                          >
                            {prod.productName}
                          </Link>
                        </li>
                      ))
                    ) : (
                      <li className="text-xs italic text-gray-500">No products</li>
                    )}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  const navLinks = (
    <>
      <Link
        to="/"
        className={`${
          isHome
            ? `text-[${gold}] border-b-2 border-[${gold}] pb-1 md:pb-0 md:border-b-0`
            : scrolled
            ? "text-black"
            : "text-white"
        }`}
        style={
          isHome
            ? { color: gold, borderBottomColor: gold }
            : undefined
        }
        onClick={handleLinkClick}
      >
        <span className="font-serif">Home</span>
      </Link>
      <Link
        to="/about"
        className={`hover:text-[${gold}] ${
          isAbout
            ? `text-[${gold}] border-b-2 border-[${gold}] pb-1 md:pb-0 md:border-b-0`
            : scrolled
            ? "text-black"
            : "text-white"
        }`}
        style={
          isAbout
            ? { color: gold, borderBottomColor: gold }
            : undefined
        }
        onClick={handleLinkClick}
      >
        <span className="font-serif">About Us</span>
      </Link>
      {productsDropdown}
      <Link
        to="/contact"
        className={`hover:text-[${gold}] ${
          isContact
            ? `text-[${gold}] border-b-2 border-[${gold}] pb-1 md:pb-0 md:border-b-0`
            : scrolled
            ? "text-black"
            : "text-white"
        }`}
        style={
          isContact
            ? { color: gold, borderBottomColor: gold }
            : undefined
        }
        onClick={handleLinkClick}
      >
        <span className="font-serif">Contact Us</span>
      </Link>
    </>
  );

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300  ${
        scrolled ? "bg-white shadow-md py-6" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2" onClick={handleLinkClick}>
          <img
            src="/logo.png"
            alt="Western Drymix logo"
            className="w-16 h-16 object-contain rounded-md bg-white"
          />
          <div className="flex flex-col leading-tight">
            <span
              className={`font-bold text-xl font-serif`}
              style={{ color: scrolled ? blue : gold }}
            >
              Western Drymix
            </span>
            <span className={`text-xs ${scrolled ? "text-gray-500" : "text-gray-100/70"}`}></span>
          </div>
        </Link>

        {/* Nav Links Desktop */}
        <nav className="hidden md:flex items-center gap-8 font-medium">
          {navLinks}
        </nav>

        {/* Right Side / Hamburger */}
        <div className="flex items-center gap-4">
          <Link
            to="/contact"
            className="hidden md:flex items-center gap-2 px-5 py-2 rounded-full transition"
            style={{
              background: gold,
              color: "#222",
              fontWeight: 600,
              boxShadow: `0 2px 8px 0 ${gold}22`,
            }}
            onClick={handleLinkClick}
          >
            <span className="font-serif">Contact us</span> <FiMail />
          </Link>
          {/* Hamburger */}
          <button
            className={`md:hidden text-2xl focus:outline-none`}
            style={{
              color: scrolled ? blue : gold
            }}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            {menuOpen ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 md:hidden">
          <div className="fixed top-0 right-0 w-3/4 max-w-xs h-full bg-white shadow-lg py-8 px-8 flex flex-col gap-8 transition-all">
            <div className="flex justify-between items-center mb-2">
              <span
                className="font-bold text-xl font-serif"
                style={{ color: gold }}
              >
                Western Drymix
              </span>
              <button
                onClick={() => setMenuOpen(false)}
                className="text-2xl"
                style={{ color: blue }}
                aria-label="Close menu"
              >
                <FiX />
              </button>
            </div>
            <nav className="flex flex-col gap-7 font-medium">
              {/* For mobile, show route navigation for Home/About Us/Products/Contact with nested products */}
              <Link
                to="/"
                className={`${
                  isHome
                    ? `border-b-2 pb-1`
                    : "hover:opacity-80"
                }`}
                style={
                  isHome
                    ? { color: gold, borderBottom: `2px solid ${gold}` }
                    : { color: blue }
                }
                onClick={handleLinkClick}
              >
                <span className="font-serif">Home</span>
              </Link>
              <Link
                to="/about"
                className={`${
                  isAbout
                    ? `border-b-2 pb-1`
                    : "hover:opacity-80"
                }`}
                style={
                  isAbout
                    ? { color: gold, borderBottom: `2px solid ${gold}` }
                    : { color: blue }
                }
                onClick={handleLinkClick}
              >
                <span className="font-serif">About Us</span>
              </Link>
              {mobileProductsList}
              <Link
                to="/contact"
                className={`${
                  isContact
                    ? `border-b-2 pb-1`
                    : "hover:opacity-80"
                }`}
                style={
                  isContact
                    ? { color: gold, borderBottom: `2px solid ${gold}` }
                    : { color: blue }
                }
                onClick={handleLinkClick}
              >
                <span className="font-serif">Contact Us</span>
              </Link>
            </nav>
            <Link
              to="/contact"
              className="flex items-center gap-2 px-5 py-2 rounded-full transition mt-6 w-fit"
              style={{
                background: gold,
                color: "#222",
                fontWeight: 600,
                boxShadow: `0 2px 8px 0 ${gold}22`,
              }}
              onClick={handleLinkClick}
            >
              <span className="font-serif">Contact us</span> <FiMail />
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
